import * as React from 'react';
import { DatePicker, DateTimePicker, DatePickerProps, DateTimePickerProps } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { dayjs } from '../ex_dayjs';

/** Any value accepted as a date input by this control. */
type MDateValue = Dayjs | Date | string | number | null | undefined;

/**
 * Formats `date` as an ISO-like string (`YYYY-MM-DDTHH:mm:ss[Z]`), honoring
 * UTC conversion, time-of-day inclusion, and "end of day" adjustment.
 *
 * @param date - The date to format.
 * @param isUTC - Whether to convert the value to UTC before formatting. Defaults to `false`.
 * @param isTime - Whether to include the time-of-day component. Defaults to `false`.
 * @param isEndOfDate - Whether to shift the date to 23:59:59 of the same day. Defaults to `false`.
 * @returns The formatted date string, or `null` if `date` is falsy.
 */
export function formattedDate (date: MDateValue, isUTC = false, isTime = false, isEndOfDate = false): string | null {
    if (!date) return null;
    const FormatDate = 'YYYY-MM-DD';
    const FormatUTC = 'YYYY-MM-DDTHH:mm:ss[Z]';

    const _dateTime = dayjs(date);
    const _dateVal = dayjs(_dateTime.format(FormatDate));
    if (isUTC) {
        if (isTime) return _dateTime.utc().format(FormatUTC);
        else if (isEndOfDate) return _dateVal.utc().add(1, 'day').add(-1, 'second').format(FormatUTC); // end of day
        else return _dateVal.utc().format(FormatUTC);
    } else {
        if (isTime) return _dateTime.format(FormatUTC);
        else if (isEndOfDate) return _dateVal.add(1, 'day').add(-1, 'second').format(FormatUTC); // end of day
        else return _dateVal.format(FormatUTC);
    }
}

/**
 * Props for {@link MDateControl}.
 */
interface MDateControlProps extends Omit<DatePickerProps<Dayjs>, 'value' | 'defaultValue' | 'onChange' | 'label' | 'format'> {
    /** `dayjs` format string. Time formats (containing `:`) render a `DateTimePicker`. Defaults to `'MM/DD/YYYY'`. */
    format?: string;
    /** Whether the reported value is converted to UTC. Defaults to `false`. */
    isUTC?: boolean;
    /** Whether the reported value is shifted to the end of the selected day. Defaults to `false`. */
    isEndOfDate?: boolean;
    /** Field label (use when the control does not have an explanatory label elsewhere). */
    label?: string;
    /** Placeholder shown in the empty input. */
    placeholder?: string;
    /** Controlled value. */
    value?: MDateValue;
    /** Initial value applied whenever it changes. */
    defaultValue?: MDateValue;
    /**
     * Called with the formatted date string (see {@link formattedDate}), or
     * `null` if the picker was cleared or the value is invalid.
     */
    onChange: (formatted: string | null) => void;
    /** `TextField` variant forwarded to the picker's text field slot. */
    variant?: 'standard' | 'outlined' | 'filled';
}

/**
 * A MUI X {@link DatePicker} / {@link DateTimePicker} (chosen based on whether
 * `format` includes a time component) wired to a `dayjs`-based
 * `value`/`defaultValue`/`onChange` contract, formatting the reported value
 * via {@link formattedDate}.
 */
const MDateControl: React.FC<MDateControlProps> = ({
    format = 'MM/DD/YYYY',
    isUTC = false,
    isEndOfDate = false,
    label, placeholder, value, defaultValue, onChange, ...props
}) => {
    const [mValue, setMValue] = React.useState<Dayjs | null>(null);
    const isTime = format.includes(':');

    React.useEffect(() => {
        if (value) setMValue(dayjs(value));
    }, [value]);

    React.useEffect(() => {
        if (defaultValue) setMValue(dayjs(defaultValue));
        else setMValue(null);
    }, [defaultValue]);

    const handleChange = (newValue: Dayjs | null) => {
        setMValue(newValue);
        onChange(isNaN(newValue?.toDate().getTime() as number) ? null : formattedDate(newValue, isUTC, isTime, isEndOfDate));
    };

    return isTime ?
        <DateTimePicker
            {...(props as Partial<DateTimePickerProps<Dayjs>>)}
            label={label}
            value={mValue}
            format={format}
            onChange={handleChange}
            slotProps={{
                clearButton: { size: 'small' },
                clearIcon: { style: { fontSize: '9px' } },
                openPickerButton: { style: { padding: 0 } },
                field: { clearable: true },
                textField: { variant: props.variant, placeholder: placeholder || undefined }
            }}
        /> :
        <DatePicker
            {...props}
            label={label}
            value={mValue}
            format={format}
            onChange={handleChange}
            slotProps={{
                clearButton: { size: 'small' },
                clearIcon: { style: { fontSize: '9px' } },
                openPickerButton: { style: { padding: 0 } },
                field: { clearable: true },
                textField: { variant: props.variant, placeholder: placeholder || undefined }
            }}
        />;
};

export default React.memo(MDateControl);
